import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const proseClass = 'max-w-2xl mx-auto px-4 py-6 text-sm text-gray-700 dark:text-night-text leading-relaxed [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-gray-900 dark:[&_h2]:text-night-text [&_h2]:mt-6 [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-gray-900 dark:[&_h3]:text-night-text [&_h3]:mt-4 [&_h3]:mb-1.5 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_li]:mb-1 [&_strong]:text-gray-900 dark:[&_strong]:text-night-text [&_a]:text-primary-600 dark:[&_a]:text-primary-400 [&_a]:underline';

export default function PrivacyPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-night-bg safe-area-top safe-area-bottom">
      <header className="sticky top-0 z-10 safe-area-top bg-white dark:bg-night-surface border-b border-gray-100 dark:border-night-card">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-2">
          <button onClick={() => navigate(-1)} aria-label="Go back" className="p-2 -ml-2 rounded-lg hover:bg-gray-100 dark:hover:bg-night-card text-gray-700 dark:text-night-text">
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-base font-semibold text-gray-900 dark:text-night-text">Privacy Policy</h1>
        </div>
      </header>

      <main className={proseClass}>
        <p className="text-sm text-gray-500 dark:text-night-muted">
          <strong>Cooksmith</strong> — A product by Wolf Technologies Ltd<br />
          Last updated: 17 April 2026
        </p>

        <h2>1. Introduction</h2>
        <p>Cooksmith is a recipe management app developed and operated by Wolf Technologies Ltd ("we", "us", "our"). We are committed to protecting your privacy and handling your personal data responsibly. This Privacy Policy explains what data we collect, how we use it, and your rights under applicable data protection laws including the UK GDPR.</p>
        <p>This policy applies to the Cooksmith app (available on iOS and the web) and any associated services.</p>

        <h2>2. Data We Collect</h2>
        <p>We collect only the minimum data necessary to provide and improve the Cooksmith experience.</p>
        <h3>Account Information</h3>
        <ul>
          <li><strong>Email address</strong> — used for authentication and account recovery, collected during sign-up via AWS Cognito or Sign In with Apple.</li>
          <li><strong>Display name</strong> — an optional name you provide for your profile.</li>
        </ul>
        <h3>App Data</h3>
        <p>When you use Cooksmith, we store the following data associated with your account:</p>
        <ul>
          <li><strong>Saved recipes</strong> — recipes you add to your personal cookbook.</li>
          <li><strong>Meal plans</strong> — weekly meal plans you create.</li>
          <li><strong>Grocery lists</strong> — shopping lists generated from your meal plans or created manually.</li>
          <li><strong>Preferences</strong> — your chosen measurement units, dietary preferences, and app settings.</li>
          <li><strong>Community ratings</strong> — ratings you submit for recipes.</li>
          <li><strong>Cooking sessions</strong> — records of recipes you have cooked.</li>
        </ul>

        <h2>3. Data We Do NOT Collect</h2>
        <ul>
          <li><strong>Location data</strong> — we do not access or store your geographic location.</li>
          <li><strong>Contacts</strong> — we do not access your address book or contacts list.</li>
          <li><strong>Photos or camera data</strong> — we do not access your camera or photo library.</li>
          <li><strong>Health data</strong> — we do not access Apple Health, Google Fit, or any health-related information.</li>
          <li><strong>Browsing history</strong> — we do not track your activity outside of Cooksmith.</li>
          <li><strong>Advertising identifiers</strong> — we do not collect device advertising IDs or use any ad-tracking technology.</li>
          <li><strong>Analytics or telemetry</strong> — we do not use third-party analytics SDKs.</li>
        </ul>

        <h2>4. How We Use Your Data</h2>
        <p>We use your data solely to provide the Cooksmith service:</p>
        <ul>
          <li><strong>Account management</strong> — to create and maintain your account, authenticate you, and enable password recovery.</li>
          <li><strong>Recipe sync</strong> — to synchronise your saved recipes, cookbook, and preferences across your devices.</li>
          <li><strong>Meal planning</strong> — to store and retrieve your meal plans and generate grocery lists.</li>
          <li><strong>Service improvement</strong> — to understand aggregate usage patterns in order to improve the recipe catalogue. This is done using anonymised, aggregated data only.</li>
        </ul>
        <p>We do not use your data for advertising, profiling, or automated decision-making.</p>

        <h2>5. Data Storage and Security</h2>
        <ul>
          <li><strong>Region</strong> — all data is stored in the AWS EU (London) region (eu-west-2), located in the United Kingdom.</li>
          <li><strong>Encryption at rest</strong> — all data stored in DynamoDB and S3 is encrypted using AWS-managed encryption keys (AES-256).</li>
          <li><strong>Encryption in transit</strong> — all communication between your device and our servers uses HTTPS (TLS 1.2+).</li>
          <li><strong>Authentication</strong> — user authentication is handled by AWS Cognito, which provides secure token-based access.</li>
          <li><strong>Access control</strong> — API access is restricted to authenticated users. Each user can only access their own data.</li>
        </ul>

        <h2>6. Third-Party Services</h2>
        <p>We use the following third-party services to operate Cooksmith:</p>
        <ul>
          <li><strong>AWS Cognito</strong> — user authentication (email address, hashed password).</li>
          <li><strong>AWS DynamoDB</strong> — data storage (all app data associated with your user ID).</li>
          <li><strong>AWS S3</strong> — recipe image storage (public recipe photos only).</li>
        </ul>
        <p>Each third-party service operates under its own privacy policy. See <a href="https://aws.amazon.com/privacy/" target="_blank" rel="noopener noreferrer">AWS Privacy Policy</a> and <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer">Apple Privacy Policy</a>.</p>

        <h2>7. Data Sharing</h2>
        <p><strong>We do not sell your personal data.</strong> We never have and never will. We may disclose data if required to do so by law, regulation, or court order. We do not share your data with advertisers, data brokers, or any other third parties.</p>

        <h2>8. Data Retention and Deletion</h2>
        <ul>
          <li><strong>Active accounts</strong> — your data is retained for as long as your account remains active.</li>
          <li><strong>Account deletion</strong> — if you request deletion, we will permanently remove all of your personal data within 30 days.</li>
        </ul>
        <p>To delete your account, use the "Delete Account" option in Settings within the app, or email <strong>pete@wolftechnologies.co.uk</strong>.</p>

        <h2>9. Your Rights</h2>
        <p>Under the UK GDPR, you have the right to access, rectify, erase, port, restrict, and object to processing of your personal data. To exercise any of these rights, contact us at <strong>pete@wolftechnologies.co.uk</strong>. We will respond within 30 days.</p>
        <p>If unsatisfied, you may lodge a complaint with the <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">Information Commissioner's Office (ICO)</a>.</p>

        <h2>10. Changes to This Policy</h2>
        <p>We may update this policy from time to time. Material changes will be communicated via email or in-app notice.</p>

        <h2>11. Contact Us</h2>
        <p>Wolf Technologies Ltd<br />Email: <strong>pete@wolftechnologies.co.uk</strong></p>
      </main>
    </div>
  );
}
